import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminTitle, DataTable } from "@/pages/admin/AdminLayout";
import { Spinner } from "@/components/site/Bits";
import { useApi, useDownload, errMsg, fmtDateTime } from "@/lib/api";

const ROLE_AR = { admin: "مدير", student: "متدرب" };
const SEND_AR = { sent: "أُرسل", failed: "فشل", skipped: "—", not_configured: "غير مفعّل" };

export default function System() {
  const api = useApi();
  const download = useDownload();
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    const [users, audit, messages, notes] = await Promise.all(["/users", "/admin/audit", "/admin/messages", "/admin/notifications"].map((u) => api("get", u)));
    setD({ users, audit, messages, notes });
  }, [api]);
  useEffect(() => {
    load();
  }, [load]);
  const setRole = async (uid, role) => {
    try { await api("patch", `/users/${uid}/role`, { role }); toast.success("تم تحديث الصلاحية"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const verify = async (uid) => {
    try { await api("post", `/users/${uid}/verify`); toast.success("تم تأكيد بريد المستخدم"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <Spinner />;
  return (
    <div data-testid="admin-system">
      <AdminTitle title="المستخدمون والسجلات">
        <button className="btn-gold-outline !py-2.5" onClick={() => download("/admin/backup", "himma-backup.json").catch(() => toast.error("تعذر التنزيل"))} data-testid="backup-btn"><DatabaseBackup size={16} />نسخة احتياطية للبيانات</button>
      </AdminTitle>
      <Tabs defaultValue="users" dir="rtl">
        <TabsList className="flex-wrap h-auto bg-white border p-1 mb-6">
          <TabsTrigger value="users" data-testid="system-tab-users">المستخدمون</TabsTrigger>
          <TabsTrigger value="notes" data-testid="system-tab-notifications">سجل الإشعارات</TabsTrigger>
          <TabsTrigger value="messages" data-testid="system-tab-messages">رسائل التواصل</TabsTrigger>
          <TabsTrigger value="audit" data-testid="system-tab-audit">سجل العمليات الإدارية</TabsTrigger>
        </TabsList>
        <TabsContent value="users">
          <DataTable head={["الاسم", "البريد", "الهاتف", "تأكيد البريد", "الصلاحية"]} testId="users-table">
            {d.users.users.map((u) => (
              <tr key={u.user_id}><td>{u.name || "—"}</td><td dir="ltr" className="text-right">{u.email}</td><td dir="ltr" className="text-right">{u.phone}</td>
                <td>{u.email_verified ? <span className="text-xs text-emerald-700" data-testid={`verified-${u.user_id}`}>مؤكد</span> : (
                  <button onClick={() => verify(u.user_id)} className="text-xs btn-gold-outline !py-1 !px-3" data-testid={`verify-user-${u.user_id}`}>تأكيد يدوياً</button>)}</td>
                <td><Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)} dir="rtl"><SelectTrigger className="w-32 h-9" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{d.users.roles.map((r) => <SelectItem key={r} value={r}>{ROLE_AR[r] || r}</SelectItem>)}</SelectContent></Select></td></tr>
            ))}
          </DataTable>
        </TabsContent>
        <TabsContent value="notes">
          <DataTable head={["النوع", "العنوان", "الجهة", "البريد", "واتساب", "التاريخ"]} testId="notifications-table">
            {d.notes.map((n) => (
              <tr key={n.id}><td className="text-xs">{n.type}</td><td>{n.title}</td><td>{n.audience === "admin" ? "الإدارة" : "متدرب"}</td>
                <td className="text-xs">{SEND_AR[n.email_status] || n.email_status}</td><td className="text-xs">{SEND_AR[n.whatsapp_status] || n.whatsapp_status}</td><td className="text-xs whitespace-nowrap">{fmtDateTime(n.created_at)}</td></tr>
            ))}
          </DataTable>
        </TabsContent>
        <TabsContent value="messages">
          <DataTable head={["الاسم", "البريد", "الهاتف", "الرسالة", "التاريخ"]} testId="messages-table">
            {d.messages.map((m) => (
              <tr key={m.id}><td>{m.name}</td><td dir="ltr" className="text-right">{m.email}</td><td dir="ltr">{m.phone}</td><td className="max-w-[360px] whitespace-pre-line">{m.message}</td><td className="text-xs whitespace-nowrap">{fmtDateTime(m.created_at)}</td></tr>
            ))}
          </DataTable>
        </TabsContent>
        <TabsContent value="audit">
          <DataTable head={["العملية", "التفاصيل", "المدير", "التاريخ"]} testId="audit-table">
            {d.audit.map((a) => (
              <tr key={a.id}><td>{a.action}</td><td className="max-w-[360px] truncate text-xs">{a.details}</td><td dir="ltr" className="text-xs">{a.admin_email}</td><td className="text-xs whitespace-nowrap">{fmtDateTime(a.created_at)}</td></tr>
            ))}
          </DataTable>
        </TabsContent>
      </Tabs>
    </div>
  );
}
